if (!customElements.get('blog-pagination')) {
  class BlogPagination extends HTMLElement {
    constructor () {
      super()
      // DOM elements
      this.grid = this.querySelector('[data-posts-grid]')
      this.loadMoreBtn = this.querySelector('[data-load-more]')
      this.buttonTextDefault = this.loadMoreBtn?.dataset.textDefault
      this.buttonTextLoading = this.loadMoreBtn?.dataset.textLoading

      // Core pagination state
      this.page = 0
      this.itemsLoad = parseInt(this.dataset.itemsLoad || 12)
      this.isLoading = false

      // Post tracking
      const parsedTotalCount = parseInt(this.dataset.totalCount || 0)
      this.totalCount = isNaN(parsedTotalCount) ? Infinity : parsedTotalCount
      this.blogUrls = this.getBlogUrls()
      this.seenPostIds = new Set()
      this.remainingPostsToRender = []
      this.postCount = 0
      this.allBlogsExhausted = false

      // Initialize
      this.setup()
    }

    setup () {
      if (this.loadMoreBtn) {
        this.loadMoreBtn.addEventListener('click', this.loadPosts.bind(this))
      }
      this.loadPosts()
    }

    resetPagination () {
      this.page = 0
      this.seenPostIds = new Set()
      this.remainingPostsToRender = []
      this.postCount = 0
      this.allBlogsExhausted = false

      if (this.grid) {
        this.grid.innerHTML = ''
      }

      this.setButtonState('default')
    }

    async loadPosts () {
      if (this.isLoading) return

      this.isLoading = true
      this.setButtonState('loading')

      try {
        // Use posts we've already fetched if available
        if (this.remainingPostsToRender.length >= this.itemsLoad) {
          const postsToRender = this.remainingPostsToRender.splice(0, this.itemsLoad)
          this.renderPosts(postsToRender)
        } else {
          // Fetch more posts
          this.page++
          const newPosts = await this.fetchNewPosts(this.page)
          this.remainingPostsToRender = [...this.remainingPostsToRender, ...newPosts]

          // Render posts if available
          if (this.remainingPostsToRender.length > 0) {
            const postsToRender = this.remainingPostsToRender.splice(0, this.itemsLoad)
            this.renderPosts(postsToRender)
          } else {
            // No more posts found in this fetch
            this.allBlogsExhausted = true
          }
        }
      } catch (error) {
        console.error('Error loading blog posts:', error)
      } finally {
        this.isLoading = false
        this.updateButtonVisibility()
      }
    }

    updateButtonVisibility () {
      // Hide button only when we've reached the end of available content
      const reachedTotalCount = this.totalCount !== Infinity && this.postCount >= this.totalCount
      const noMorePosts = this.remainingPostsToRender.length === 0

      if ((reachedTotalCount && noMorePosts) || (this.allBlogsExhausted && noMorePosts)) {
        this.setButtonState('hidden')
      } else {
        this.setButtonState('default')
      }
    }

    setButtonState (state) {
      if (!this.loadMoreBtn) return

      this.loadMoreBtn.classList.remove('is-loading', 'is-hidden')

      if (state === 'loading') {
        this.loadMoreBtn.classList.add('is-loading')
        this.loadMoreBtn.textContent = this.buttonTextLoading
      } else if (state === 'hidden') {
        this.loadMoreBtn.classList.add('is-hidden')
      } else {
        this.loadMoreBtn.textContent = this.buttonTextDefault
      }
    }

    sortPosts (posts) {
      return [...posts].sort((a, b) => {
        const aTime = parseInt(a.dataset.published || 0) || 0
        const bTime = parseInt(b.dataset.published || 0) || 0
        return bTime - aTime
      })
    }

    renderPosts (posts) {
      if (!posts.length || !this.grid) return

      const sortedPosts = this.sortPosts(posts)
      const postCount = this.grid.querySelectorAll('.blg-Blogpost_Card, [data-blog-post]').length

      let currentCount = postCount
      sortedPosts.forEach(post => {
        this.grid.appendChild(post)
        currentCount++
        this.postCount++

        this.insertPromoBarsIfNeeded(currentCount)
      })
    }

    insertPromoBarsIfNeeded (postCount) {
      if (postCount === 2) {
        this.insertPromoBar('email')
      } else if (postCount === 4) {
        this.insertPromoBar('cta')
      }
    }

    insertPromoBar (type) {
      const selector = type === 'email' ? '[data-email-bar-inserted]' : '[data-cta-bar-inserted]'
      const templateSelector = type === 'email' ? '[data-email-bar-template]' : '[data-cta-bar-template]'

      if (this.grid.querySelector(selector)) return

      const template = document.querySelector(templateSelector)
      if (!template) return

      const clone = template.cloneNode(true)
      clone.style.display = 'block'
      clone.setAttribute(`data-${type}-bar-inserted`, 'true')

      const column = this.createPromoBarContainer(clone)
      this.grid.appendChild(column)
    }

    createPromoBarContainer (content) {
      const column = document.createElement('div')
      column.className = 'lyt-FullWidthColumn'

      const container = document.createElement('div')
      container.className = 'lyt-Container_Negative'

      container.appendChild(content)
      column.appendChild(container)

      return column
    }

    async fetchNewPosts (page) {
      const newPosts = []
      let anyBlogHadPosts = false

      for (const blogUrl of this.blogUrls) {
        try {
          const pageUrl = `${blogUrl}?view=ajax&page=${page}`
          const response = await fetch(pageUrl)
          const html = await response.text()

          const doc = new DOMParser().parseFromString(html, 'text/html')
          const items = doc.querySelectorAll('[data-blog-post]')

          if (items.length > 0) {
            anyBlogHadPosts = true

            for (const item of items) {
              const postId = this.extractPostId(item, blogUrl)

              // Skip posts we've already seen
              if (this.seenPostIds.has(postId)) continue

              this.seenPostIds.add(postId)

              // Add metadata
              const publishedAt = item.querySelector('time')?.getAttribute('datetime') || ''
              if (publishedAt) {
                item.dataset.published = new Date(publishedAt).getTime()
              }

              item.dataset.sourceBlog = blogUrl
              item.dataset.sourcePage = page
              item.dataset.postId = postId

              newPosts.push(item)
            }
          }
        } catch (error) {
          console.error(`Error fetching from ${blogUrl} page ${page}:`, error)
        }
      }

      // Only update exhausted state if we didn't find any posts across all blogs
      this.allBlogsExhausted = !anyBlogHadPosts && page > 1

      return newPosts
    }

    extractPostId (postElement, blogUrl) {
      // Try data attributes first
      const postId = postElement.dataset.postId || postElement.dataset.id
      if (postId) return `${blogUrl}-${postId}`

      // Try to extract from URL
      const linkElement = postElement.querySelector('a[href*="/blogs/"]')
      if (linkElement) return linkElement.getAttribute('href')

      // Fallback: use content hash
      const content = postElement.innerHTML
      return `${blogUrl}-${content.length}-${content.slice(0, 100)}`
    }

    getBlogUrls () {
      return this.dataset.blogUrls.split(',').filter(url => url && url.trim())
    }
  }

  customElements.define('blog-pagination', BlogPagination)
}
